export const MIN_FILE_SIZE = 1024;
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Mirrors `file_security.project_reference.pdf.max_page_count` on the API.
// Keep the two in lockstep — when this changes, change both.
export const MAX_PDF_PAGES = 100;

export const DEFAULT_ALLOWED_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

export const IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

const EXTENSION_MIME_MAP: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  pdf: 'application/pdf',
  txt: 'text/plain',
  md: 'text/markdown',
};

export function getEffectiveType(file: File): string {
  if (file.type) return file.type.toLowerCase();
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  return EXTENSION_MIME_MAP[ext] || '';
}

export function isImageFile(file: File): boolean {
  return IMAGE_TYPES.includes(getEffectiveType(file));
}

/**
 * Validation failures are returned as i18n codes + ICU params — message
 * selection happens at the toast call site (component layer) via the
 * `fileUpload` namespace, never inside this util.
 */
export type FileValidationErrorCode =
  | 'fileTooSmall'
  | 'fileTooLarge'
  | 'unsupportedTypeImages'
  | 'unsupportedTypeList';

export interface FileValidationError {
  code: FileValidationErrorCode;
  params: Record<string, string | number>;
}

export interface FileValidationResult {
  valid: boolean;
  error?: FileValidationError;
}

export function validateFile(
  file: File,
  allowedTypes: string[] = DEFAULT_ALLOWED_TYPES
): FileValidationResult {
  if (file.size < MIN_FILE_SIZE) {
    return {
      valid: false,
      error: { code: 'fileTooSmall', params: { name: file.name } },
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: {
        code: 'fileTooLarge',
        params: {
          name: file.name,
          size: (file.size / (1024 * 1024)).toFixed(1),
          max: (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0),
        },
      },
    };
  }

  const effectiveType = getEffectiveType(file);
  if (!allowedTypes.includes(effectiveType)) {
    return {
      valid: false,
      error:
        allowedTypes === DEFAULT_ALLOWED_TYPES
          ? { code: 'unsupportedTypeImages', params: { name: file.name } }
          : {
              code: 'unsupportedTypeList',
              params: { name: file.name, types: allowedTypes.join(', ') },
            },
    };
  }

  return { valid: true };
}

/**
 * PDF spec §7.5.2 — every PDF starts with `%PDF-` (5 ASCII bytes).
 * Cheap guard against renamed-extension uploads before we run the parser.
 */
async function hasPdfMagicBytes(file: File): Promise<boolean> {
  const head = await file.slice(0, 5).arrayBuffer();
  const bytes = new Uint8Array(head);
  return (
    bytes.length === 5 &&
    bytes[0] === 0x25 && // %
    bytes[1] === 0x50 && // P
    bytes[2] === 0x44 && // D
    bytes[3] === 0x46 && // F
    bytes[4] === 0x2d //   -
  );
}

/**
 * Parse PDF and return page count, or null if the file isn't a real PDF.
 *
 * Scans the binary for `/Type /Pages ... /Count` (catalog), falling back to
 * counting `/Type /Page` leaves. Treats "no magic bytes" and "regex found
 * nothing" as the same signal: this isn't a usable PDF.
 *
 * Returns:
 *  - number ≥ 1: confidently parsed page count
 *  - null: file is not a PDF or is corrupted (caller must reject the upload)
 */
export async function countPdfPages(file: File): Promise<number | null> {
  if (getEffectiveType(file) !== 'application/pdf') return null;
  if (!(await hasPdfMagicBytes(file))) return null;

  try {
    const buffer = await file.arrayBuffer();
    // Latin-1 keeps every byte as a 1:1 codepoint — needed because PDFs mix
    // ASCII keywords with binary stream content; UTF-8 decoding would corrupt
    // the byte stream and miscount.
    const text = new TextDecoder('latin1').decode(buffer);

    // Catalog node: `/Type /Pages ... /Count N` is authoritative on most PDFs.
    const countMatch = text.match(/\/Type\s*\/Pages[^/]*?\/Count\s+(\d+)/);
    if (countMatch?.[1]) {
      const n = Number.parseInt(countMatch[1], 10);
      if (n > 0) return n;
    }

    // Fallback: enumerate leaf `/Type /Page` nodes. Negative lookahead skips
    // the parent `/Pages` catalog and any `/PageLabel`-style derivatives.
    const pages = text.match(/\/Type\s*\/Page(?![s/A-Za-z])/g);
    return pages && pages.length > 0 ? pages.length : null;
  } catch {
    return null;
  }
}

export interface ReferenceCostEstimate {
  /** Internal-token cost rounded up to 1 decimal for display + comparison. */
  estimate: number;
  /** Per-file breakdown — kept for diagnostics, not displayed. */
  breakdown: Array<{ name: string; cost: number; pages?: number }>;
}

/**
 * Project pre-upload internal-token cost for a batch of references.
 *
 * - PDFs: pages × cost_per_page (parser-failed PDFs contribute 0 — same
 *   trade-off as the page-count limit gate).
 * - text/markdown: file.size × cost_per_char. Bytes ≥ chars for any UTF-8
 *   text, so this conservatively over-estimates for cyrillic/CJK and stays
 *   accurate for latin — preferred direction for a balance gate.
 * - Images and unknown types contribute 0 (no LLM cost on the upload path).
 */
export async function estimateReferenceCost(
  files: File[],
  pricing: { cost_per_page_internal: number; cost_per_char_internal: number }
): Promise<ReferenceCostEstimate> {
  const breakdown: ReferenceCostEstimate['breakdown'] = [];
  let total = 0;

  for (const file of files) {
    const type = getEffectiveType(file);
    if (type === 'application/pdf') {
      const pages = await countPdfPages(file);
      const cost = (pages ?? 0) * pricing.cost_per_page_internal;
      total += cost;
      breakdown.push({ name: file.name, cost, pages: pages ?? undefined });
    } else if (type === 'text/plain' || type === 'text/markdown') {
      const cost = file.size * pricing.cost_per_char_internal;
      total += cost;
      breakdown.push({ name: file.name, cost });
    } else {
      breakdown.push({ name: file.name, cost: 0 });
    }
  }

  return { estimate: Math.ceil(total * 10) / 10, breakdown };
}
